import * as React from "react";

import {
  filterToCondition,
  type ActiveFiltersState,
  type Filter,
  type FilterDefinition,
} from "@/shared/components/ResourceView";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { composeQueryBody } from "@/shared/domain/query";
import type { SearchQueryBody } from "@/types";

import {
  SESSION_INGESTION_FILTERS,
  SESSION_OPTION_CONDITIONS,
} from "./coreSessionFilters";

/**
 * The filter rows a view of sessions offers: the mount's own, then Status and
 * "Fully ingested at" when the `release-session-ingestion` flag is on for
 * `orgId`, then the org's custom fields. An `undefined` or empty
 * `mountDefinitions` is returned unchanged, so a mount that hides its filters
 * gets no rows. Every view of sessions builds its rows here, so a filter means
 * the same thing on each of them.
 */
export function useSessionFilterDefinitions(
  orgId: string | undefined,
  mountDefinitions: FilterDefinition[] | undefined,
  customFieldDefinitions: FilterDefinition[],
): FilterDefinition[] | undefined {
  const ingestionEnabled = useCheckFeatureFlag(
    "release-session-ingestion",
    orgId,
  );
  return React.useMemo(
    () =>
      mountDefinitions?.length
        ? [
            ...mountDefinitions,
            ...(ingestionEnabled ? SESSION_INGESTION_FILTERS : []),
            ...customFieldDefinitions,
          ]
        : mountDefinitions,
    [mountDefinitions, ingestionEnabled, customFieldDefinitions],
  );
}

export interface SessionsQueryInput {
  /**
   * Filters the user cannot see or widen (a device or dataset tab's own
   * id), joined to theirs under an outer AND whatever their match mode.
   */
  scope: Filter[] | undefined;
  /** The user's filters. Only structured ones narrow the query. */
  filters: ActiveFiltersState;
  /** The rows the filters were built against, from {@link useSessionFilterDefinitions}. */
  definitions: FilterDefinition[] | undefined;
  sort?: { columnId: string; direction: "asc" | "desc" } | null;
}

/**
 * The query body a view of sessions submits: `scope` AND the user's structured
 * filters. `definitions` keeps rows on id fields free of `IS_NULL` /
 * `IS_NOT_NULL`, which the backend rejects there, and Status options expand to
 * the conditions they stand for (see `SESSION_OPTION_CONDITIONS`), so every
 * view sends the same condition for the same filters.
 */
export function sessionsQueryBody({
  scope,
  filters,
  definitions,
  sort,
}: SessionsQueryInput): SearchQueryBody {
  return composeQueryBody({
    scopeCondition: filterToCondition(scope ?? [], {
      matchMode: "AND",
      definitions,
    }),
    filterCondition:
      filters.type === "structured"
        ? filterToCondition(filters.filters, {
            matchMode: filters.matchMode,
            definitions,
            optionConditions: SESSION_OPTION_CONDITIONS,
          })
        : undefined,
    sort,
  });
}
